import React from 'react';
import { Button } from '../../../../../../components/Common/UI/Button';
import Icons from '../../../../../../components/Common/Icons/Icons';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/Common/UI/Select';
import { Input } from '../../../../../../components/Common/UI/Input';
import { cn } from '../../../../../../utils/styles';

export interface ICriteriaData {
  id: string;
  type: string;
  is?: boolean;
  between?: boolean;
  moreThan?: boolean;
  min?: string;
  max?: string;
  input: string;
}

export interface IOption {
  value: string;
  label: string;
}

interface ICriteriaList {
  opposite: boolean;
  options: IOption[];
  data: ICriteriaData[];
  handleRemove: (id: string) => void;
  onChange: ({ key, value }: Record<string, string>, id: string) => void;
  handleAdd: () => void;
}

function CriteriaList({ options, data, opposite, handleRemove, onChange, handleAdd }: ICriteriaList) {
  return (
    <div>
      {data.map(el => (
        <div
          key={el.id}
          className={cn(
            'grid gap-x-[10px] items-start mb-[15px]',
            {
              'grid-cols-[auto,auto,1fr,auto]': opposite,
            },
            {
              'grid-cols-[auto,1fr,auto]': !opposite,
              'grid-cols-[auto,auto,1fr,auto]': el.type === 'interestRate',
              'grid-cols-[auto,auto,auto,1fr,auto]': el.type === 'endBalance',
            }
          )}
        >
          <Select
            value={el.type}
            onValueChange={value => {
              onChange({ key: 'type', value }, el.id);
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {options.map(({ value, label }) => (
                <SelectItem key={value} value={value}>
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {opposite && el.is !== undefined ? (
            <Select
              value={el.is.toString()}
              onValueChange={value => {
                onChange({ key: 'is', value }, el.id);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Is" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Is</SelectItem>
                <SelectItem value="false">Is not</SelectItem>
              </SelectContent>
            </Select>
          ) : null}
          {el.type === 'interestRate' ? (
            <div>
              <div className="grid grid-cols-[auto,1fr,1fr] items-center gap-[10px]">
                <Select
                  value={(el?.between || true).toString()}
                  onValueChange={value => {
                    onChange({ key: 'between', value }, el.id);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Between" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Between</SelectItem>
                    <SelectItem value="false">Not Between</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={el.min}
                  onChange={e => {
                    onChange({ key: 'min', value: e.target.value }, el.id);
                  }}
                />
                <Input
                  value={el.max}
                  onChange={e => {
                    onChange({ key: 'max', value: e.target.value }, el.id);
                  }}
                />
              </div>
              <div className="mt-[5px] text-sm text-addition1-50 font-medium relative">
                The interest rate should be a two decimal point number (e.g. 2.99 which means 2.99%)
              </div>
            </div>
          ) : null}
          {el.type === 'endBalance' ? (
            <>
              <Select
                value={(el?.moreThan || true).toString()}
                onValueChange={value => {
                  onChange({ key: 'moreThan', value }, el.id);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="More Than" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">More Than</SelectItem>
                  <SelectItem value="false">Less Than</SelectItem>
                </SelectContent>
              </Select>
              <Input
                value={el.input}
                onChange={e => {
                  onChange({ key: 'input', value: e.target.value }, el.id);
                }}
              />
            </>
          ) : null}
          {el.type === 'productType' ? (
            <div>
              <Input
                value={el.input}
                onChange={e => {
                  onChange({ key: 'input', value: e.target.value }, el.id);
                }}
              />
              <div className="mt-[5px] text-sm text-addition1-50 font-medium relative">
                The product types are separated by comma (e.g $1.52).
              </div>
            </div>
          ) : null}
          {el.type === 'account' ? (
            <div>
              <div className="flex items-center gap-[10px]">
                <div className="flex items-center border border-input rounded-lg w-max">
                  <Input
                    value="Not file chosen"
                    variant="transparent"
                    inputClassName="border-none !cursor-auto"
                    disabled
                  />
                  <Button
                    className="border-none rounded-none bg-inputBg hover:bg-inputBg text-primary-text
                    hover:text-primary-text"
                  >
                    Browse
                  </Button>
                </div>
                <div className="text-primary-text">Uploaded file: Accounts LA VISA.csv</div>
              </div>
              <div className="mt-[5px] text-sm text-addition1-50 font-medium relative max-w-[300px]">
                The file uploaded should be a plain text file that contains a list of account numbers. The maximum size
                of uploaded file is 1MB.
              </div>
            </div>
          ) : null}
          {el.type === 'zipCode' ? (
            <div>
              <Input
                value={el.input}
                onChange={e => {
                  onChange({ key: 'input', value: e.target.value }, el.id);
                }}
              />
              <div className="mt-[5px] text-sm text-addition1-50 font-medium relative">
                The zip codes should be only 5 digits in length and seperated by comma. For zip ranges, the format
                should be ZIP1-ZIP2 (e.g. 91234, 92008, 93008).
              </div>
            </div>
          ) : null}
          {el.type !== 'interestRate' &&
          el.type !== 'endBalance' &&
          el.type !== 'productType' &&
          el.type !== 'zipCode' &&
          el.type !== 'account' ? (
            <Input
              value={el.input}
              onChange={e => {
                onChange({ key: 'input', value: e.target.value }, el.id);
              }}
            />
          ) : null}
          <Icons.Delete
            onClick={() => handleRemove(el.id)}
            className="[&_path]:fill-[#c6cfdb] [&_path]:hover:fill-destructive cursor-pointer mt-[10px]"
          />
        </div>
      ))}
      <Button
        variant="link"
        className="text-addition5 mt-[10px] hover:no-underline p-0 font-bold h-auto"
        onClick={handleAdd}
      >
        <Icons.PlusFilled className="[&_path]:fill-addition5 mr-[5px]" />
        Add New Criteria
      </Button>
    </div>
  );
}

export default CriteriaList;
